import Card from "components/card";
import React, { useEffect, useState, useCallback } from "react";
import TechnicalNotesDataTable from "./components/TechnicalNotesDataTable";
import Loader from "components/Loader";
import { technicalNotesApi } from "api/models";
import { TechnicalNote, TechnicalNotesListManagementParams } from "types";
import { handleAxiosAndCaughtError } from "utils";
import { useAuthContext } from "auth/AuthProvider";
import { useSearchParams, useParams } from "react-router-dom";

const TechnicalNotes = () => {
  const [technicalNotes, setTechnicalNotes] = useState<TechnicalNote[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState<number>(0);
  const { user } = useAuthContext();
  const [searchParams] = useSearchParams();
  const { id } = useParams<{ id: string }>();
  
  // Get the technical note ID from URL params (e.g., /technical-notes/123) or query params (e.g., /technical-notes?id=123)
  const technicalNoteIdFromUrl = id || searchParams.get('id');

  const fetchTechnicalNotes = useCallback(async (params?: Partial<TechnicalNotesListManagementParams>, signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      // Default parameters with workspace_id from user context
      const defaultParams: TechnicalNotesListManagementParams = {
        agent_id: null, // Allow filtering by agent_id or get all
        workspace_id: user?.current_workspace?.id || null,
        skip: 0,
        take: 20,
        ...params
      };

      const resp = await technicalNotesApi.getListManagement(defaultParams);
      setTechnicalNotes(resp.items || []);
      setTotal(resp.total || 0);
    } catch (err: any) {
      if (signal?.aborted || err.name === "CanceledError") {
        return;
      }
      setError("Click here to retry");
      setTechnicalNotes([]);
      setTotal(0);
      handleAxiosAndCaughtError(err);
    } finally {
      setLoading(false);
    }
  }, [user?.current_workspace?.id]);

  useEffect(() => {
    const abortController = new AbortController();
    fetchTechnicalNotes({}, abortController.signal);

    return () => abortController.abort();
  }, [fetchTechnicalNotes]);

  return (
    <Card extra={" relative w-full  h-[80vh] md:mt-3 mt-12 px-6 "}>
      {loading ? (
        <Loader
          txt="Loading technical notes..."
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
        />
      ) : error ? (
        <div
          className="absolute left-1/2 top-1/2 flex -translate-x-1/2
        -translate-y-1/2 flex-col items-center justify-center py-10
         "
        >
          <span className="mb-4 text-red-500">{error}</span>
          <button
            className="rounded bg-brand-500 px-4 py-2 text-white transition hover:bg-brand-600"
            onClick={() => fetchTechnicalNotes()}
          >
            Retry
          </button>
        </div>
      ) : (
        <TechnicalNotesDataTable 
          fetchTechnicalNotes={fetchTechnicalNotes} 
          tableData={technicalNotes || []} 
          total={total}
          initialTechnicalNoteId={technicalNoteIdFromUrl}
        />
      )}
    </Card>
  );
};

export default TechnicalNotes; 